import React, { useRef, useEffect, useCallback } from "react";
import { Share2, Download, Link as LinkIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const truncate = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s || "");

// Controlled brag card for the #1 clan — the whole crew can share it. Parent owns `open`.
export default function ClanCard({ clan, open, onOpenChange }) {
  const canvasRef = useRef(null);
  const shareUrl = `${window.location.origin}/app/multiplayer`;
  const shortUrl = shareUrl.replace(/^https?:\/\//, "");
  const c = clan || {};

  const draw = useCallback(() => {
    const cv = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext("2d");
    const W = 1200, H = 630;
    const accent = c.banner || "#1f6feb";
    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, "#0b1026"); grad.addColorStop(1, accent);
    ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "rgba(0,0,0,0.4)"; ctx.fillRect(0, 0, W, H);
    ctx.textBaseline = "top";
    ctx.fillStyle = "rgba(255,209,102,0.95)"; ctx.font = "bold 32px Inter, system-ui, sans-serif";
    ctx.fillText("👑 GAMEVIEW STUDIO · TOP CLAN", 60, 52);
    // emoji crest
    ctx.textBaseline = "middle"; ctx.font = "170px serif";
    ctx.fillText(c.emoji || "🛡️", 80, 340);
    ctx.textBaseline = "alphabetic"; ctx.fillStyle = "#fff"; ctx.font = "bold 66px Inter, system-ui, sans-serif";
    ctx.fillText(truncate(c.name || "The Clan", 20), 320, 250);
    if (c.tag) { ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.font = "bold 30px monospace"; ctx.fillText(`[${c.tag}]`, 320, 292); }
    ctx.fillStyle = "#ffd166"; ctx.font = "bold 96px Inter, system-ui, sans-serif";
    ctx.fillText(`${c.score || 0}`, 320, 400);
    ctx.fillStyle = "rgba(255,255,255,0.9)"; ctx.font = "bold 34px Inter, system-ui, sans-serif";
    ctx.fillText("CLAN POWER", 320, 445);
    ctx.fillStyle = "rgba(255,255,255,0.82)"; ctx.font = "28px Inter, system-ui, sans-serif";
    ctx.fillText(`🩹 ${c.rescues || 0}   💥 ${c.kills || 0}   👑 ${c.boss_kills || 0}   🏆 ${c.mvp || 0}   ·   ${c.member_count || 0} crew`, 320, 500);
    ctx.fillStyle = "rgba(255,255,255,0.72)"; ctx.textBaseline = "top"; ctx.font = "26px Inter, system-ui, sans-serif";
    ctx.fillText(`▶ Join the fight — ${truncate(shortUrl, 46)}`, 60, 578);
  }, [c.banner, c.emoji, c.name, c.tag, c.score, c.rescues, c.kills, c.boss_kills, c.mvp, c.member_count, shortUrl]);

  useEffect(() => { if (open) { const t = setTimeout(draw, 60); return () => clearTimeout(t); } }, [open, draw]);

  const toBlob = () => new Promise((res) => canvasRef.current.toBlob(res, "image/png"));
  const download = async () => {
    const blob = await toBlob(); if (!blob) return;
    const url = URL.createObjectURL(blob);
    const el = document.createElement("a");
    el.href = url; el.download = `clan-${(c.name || "top-clan").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`;
    el.click(); URL.revokeObjectURL(url);
    toast.success("Clan card downloaded!");
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied — rally the crew!"); }
    catch { toast.error("Couldn't copy the link."); }
  };
  const nativeShare = async () => {
    const text = `${c.name} is the #1 clan on GameView Studio co-op — ${c.score} clan power! 👑`;
    try {
      const blob = await toBlob();
      const file = blob ? new File([blob], "top-clan.png", { type: "image/png" }) : null;
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: c.name, text, url: shareUrl });
      } else {
        await navigator.share({ title: c.name, text, url: shareUrl });
      }
    } catch { /* cancelled / unsupported */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="clan-card-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-amber-400" /> Share your clan</DialogTitle>
          <DialogDescription>Download or share a brag card celebrating your crew's #1 spot.</DialogDescription>
        </DialogHeader>
        <canvas ref={canvasRef} width={1200} height={630} className="w-full rounded-xl border border-[hsl(var(--border))]" data-testid="clan-card-canvas" />
        <div className="flex flex-wrap gap-2">
          <Button onClick={download} className="rounded-full gap-1.5 flex-1 bg-gradient-to-r from-amber-500 to-sky-500" data-testid="clan-card-download"><Download className="w-4 h-4" /> Download image</Button>
          <Button variant="outline" onClick={copyLink} className="rounded-full gap-1.5 flex-1" data-testid="clan-card-copy"><LinkIcon className="w-4 h-4" /> Copy link</Button>
          {typeof navigator !== "undefined" && navigator.share && (
            <Button variant="outline" onClick={nativeShare} className="rounded-full gap-1.5 flex-1" data-testid="clan-card-native"><Share2 className="w-4 h-4" /> Share…</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
